import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { addYears, startOfDay } from "date-fns";
import { Box, Typography } from "@mui/material";
import {
  Account,
  AccountsApiListAndSearchAllAccountsRequest,
  AccountsApiSelectGroupsIdRequest,
} from "@bn/api/apiv1";
import { useAccountIdsByTypes } from "@api/v1/hooks/useAccountIdsByTypes";
import { CardPageLayout, CardHeading } from "@components";
import { InvestmentsActionButton } from "@features/investments/components/InvestmentsActionButton";
import { DepotsSelector } from "@features/selectDepots/DepotsSelector";
import { InvestmentsChartDataStatistic } from "@api/v1/hooks/performance/useInvestmentsChartData.ts";
import { useSelectGroupsForId } from "../../hooks/useSelectGroupsForId.ts";
import {
  DepotsDashboardChart,
  DepotsDashboardContent,
} from "../depots/components/DepotsDashboard";

const accountParams: AccountsApiListAndSearchAllAccountsRequest = {
  type: ["01_depot", "05_material_assets"],
};

const selectedGroupParams: AccountsApiSelectGroupsIdRequest = {
  id: "investmentList",
};

export const InvestmentsPage = () => {
  const { t } = useTranslation();
  const [statistic, setStatistic] = useState<InvestmentsChartDataStatistic>();

  const {
    data: selectedGroup,
    selectedAccounts,
    refetch: refetchGroups,
  } = useSelectGroupsForId({
    id: selectedGroupParams.id,
    accountParams,
  });

  const groupedAccountIds = useAccountIdsByTypes(selectedAccounts as Account[]);
  const depotAccountIds = groupedAccountIds.depotIds ?? [];
  const investmentsChartFirstDate = startOfDay(addYears(new Date(), -1));

  return (
    <CardPageLayout
      id="layout"
      data-testid="investments-page"
      breadcrumbs={[
        { to: "/", text: t("breadcrumbs.home") },
        { text: t("breadcrumbs.investments") },
      ]}
      cardHeading={
        <CardHeading
          title={
            <Box>
              <Typography variant="h4" mb={2}>
                {t("breadcrumbs.investments")}
              </Typography>
              <DepotsSelector
                selectedGroup={selectedGroup}
                accountParams={accountParams}
                selectedGroupParams={selectedGroupParams}
                color="text.secondary"
                onSave={() => refetchGroups()}
              />
            </Box>
          }
          actions={<InvestmentsActionButton />}
        />
      }
      cardChildren={
        <DepotsDashboardChart
          accountIds={depotAccountIds}
          rangeId="investments"
          showComparisonSelector
          rangeFirstDate={investmentsChartFirstDate}
          onStatistic={setStatistic}
        />
      }
    >
      {statistic && (
        <DepotsDashboardContent
          accountIds={depotAccountIds}
          statistic={statistic}
        />
      )}
    </CardPageLayout>
  );
};
